import { z } from 'zod';
import { SubParcelBriefSchema } from './subparcels.js';
import { booleanParam, PaginationMetaSchema } from './pagination.js';

/**
 * @deprecated The photo "project" is now an editable `ProjectType` reference
 * table (2026-06-09), not a fixed enum. `project` is a free-text `z.string()`
 * everywhere. This alias is retained only so the web/mobile slices keep
 * compiling until they migrate off it; new code must NOT use it.
 */
export const PhotoProjectSchema = z.string();
/**
 * @deprecated Was the union of the five legacy enum codes; now `string`
 * because projects are managed in the `project_type` table. Do not use.
 */
export type PhotoProject = string;

/**
 * @deprecated The canonical list now lives in the `project_type` reference
 * table (`GET /api/regions/project-types`). These five legacy codes are kept
 * only so existing web pickers keep rendering until their slice migrates.
 */
export const PHOTO_PROJECTS = ['PNERP', 'ORANGE', 'WORLD_BANK', 'FORESTRY_FUND', 'OTHER'] as const;

/**
 * @deprecated Projects are now free text backed by the `project_type` table;
 * no coercion to a closed enum is required. This shim maps the legacy enum
 * codes to the new canonical Romanian labels for any caller that still feeds
 * it old codes, and otherwise passes the trimmed input through unchanged
 * (empty / null -> 'PNERP', the default). New code should store the raw
 * project name and let the reference table validate it.
 */
export function coercePhotoProject(v: unknown): string {
  if (v === null || v === undefined) return 'PNERP';
  const s = String(v).trim();
  if (s === '') return 'PNERP';
  const normalised = s.toUpperCase().replace(/[\s\-]+/g, '_');
  const legacyMap: Record<string, string> = {
    FORESTRY_FUND: 'Fondul Forestier',
    FORESTRYFUND: 'Fondul Forestier',
    FOND_SILVIC: 'Fondul Forestier',
    FONDSILVIC: 'Fondul Forestier',
    OTHER: 'Alte',
    ALT: 'Alte',
    ALTE: 'Alte',
    ALTUL: 'Alte',
    ДРУГОЕ: 'Alte',
  };
  // PNERP / ORANGE / WORLD_BANK remain valid labels — pass through.
  return legacyMap[normalised] ?? s;
}

export const PhotoAuthorBriefSchema = z.object({
  id: z.string().uuid(),
  fullName: z.string(),
  cod: z.string().nullable().optional(),
});
export type PhotoAuthorBrief = z.infer<typeof PhotoAuthorBriefSchema>;

/**
 * The linked polygon's own values, carried on every photo.
 *
 * For a linked photo this is the authoritative source of the location
 * metadata: the photo's copies of ucf / raion / primarie / ocolSilvic /
 * entitateSilvica / codProiect are deliberately nulled when a parcel is
 * attached (the edit dialog inherits them from the parcel), so the UI
 * must read them from here and only fall back to the photo's own fields
 * when there is no polygon.
 */
export const PhotoParcelBriefSchema = z.object({
  id: z.string().uuid(),
  ucf: z.string(),
  codProiect: z.string().nullable(),
  /** The polygon's source ID (`ID_Cod`) — the shapefile join key. */
  projectId: z.string().nullable(),
  raion: z.string(),
  primarie: z.string(),
  ocolSilvic: z.string().nullable(),
  entitateSilvica: z.string().nullable(),
  /** Hectares, as recorded on the polygon. */
  suprafata: z.number(),
  anul: z.number().int(),
});
export type PhotoParcelBrief = z.infer<typeof PhotoParcelBriefSchema>;

export const PhotoRecordSchema = z.object({
  id: z.string().uuid(),
  /** Short human-readable photo number, unique and immutable. Use it in
   *  UI and when a person has to quote a photo; `id` stays the key. */
  nr: z.number().int(),
  userId: z.string().uuid(),
  authorId: z.string().uuid().nullable(),
  importBatchId: z.string().uuid().nullable(),
  parcelId: z.string().uuid().nullable(),
  parcelLabel: z.string().nullable(),
  fileName: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  /** GPS accuracy radius in meters reported by the device at capture
   *  time. Null on legacy rows / xlsx imports / web uploads. */
  gpsAccuracyM: z.number().nullable().optional(),
  downloadUrl: z.string(),
  ucf: z.string().nullable(),
  suprafata: z.number().nullable(),
  raion: z.string().nullable(),
  primarie: z.string().nullable(),
  ocolSilvic: z.string().nullable(),
  entitateSilvica: z.string().nullable(),
  /** Species code (Specia de Baza) — FK-free ref to tree_species.cod. */
  speciaDeBaza: z.string().nullable(),
  /** Year the silviculture culture was created (Anul creării Culturii silvice). */
  anulCultura: z.number().int().nullable(),
  /** Last executed work (Ultima lucrare executată) — FK-free ref to work_type.name. */
  ultimaLucrare: z.string().nullable(),
  /** Primarie cod (Primarii_Raion_Cod) — FK-free ref to primarie.cod. */
  codPrimarie: z.string().nullable(),
  /** @deprecated Use `year`. Retained one release for client BC. */
  anul: z.number().int().nullable(),
  year: z.number().int().nullable(),
  month: z.number().int().min(1).max(12).nullable(),
  /** Free-text project / funding stream — a `project_type.name` reference. */
  project: z.string(),
  projectNote: z.string().nullable(),
  codProiect: z.string().nullable(),
  polygonStatus: z.string(),
  author: PhotoAuthorBriefSchema.nullable(),
  parcel: PhotoParcelBriefSchema.nullable(),
  /** The subpolygon of `parcel` the photo's GPS point falls in, resolved
   *  on read. Null when unlinked, outside every subpolygon, or on reads
   *  that do not resolve it (create / update responses). */
  subParcel: SubParcelBriefSchema.nullable().default(null),
  /** Number of comments on this photo. Populated by the list endpoint;
   *  0 in contexts that don't aggregate it (single-record reads). */
  commentCount: z.number().int().nonnegative().default(0),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
});
export type PhotoRecordDto = z.infer<typeof PhotoRecordSchema>;

export const PhotoRecordListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(500).default(50),
  search: z.string().optional(),
  raion: z.string().optional(),
  raioane: z.string().optional(), // csv; takes precedence over `raion`
  /** Legacy alias for `year`. Accepted for one release. */
  anul: z.coerce.number().int().optional(),
  year: z.coerce.number().int().optional(),
  years: z.string().optional(), // csv of ints; takes precedence over `year`/`anul`
  anulMin: z.coerce.number().int().optional(),
  anulMax: z.coerce.number().int().optional(),
  month: z.coerce.number().int().min(1).max(12).optional(),
  semester: z.coerce.number().int().min(1).max(2).optional(),
  parcelId: z.string().uuid().optional(),
  parcelIds: z.string().optional(), // csv of uuids
  project: z.string().optional(), // csv of PhotoProject values
  authorId: z.string().uuid().optional(),
  /**
   * Filter by one or more author `cod` short codes (CSV). When non-empty,
   * matches photos whose `author.cod` is in the list. Distinct from
   * `authorId` (UUID); meant for the user-facing multi-select where cods
   * are short, human-readable, and stable across UI surfaces.
   */
  authorCods: z.string().optional(),
  /**
   * Filter by one or more `entitateSilvica` values (CSV). Empty slot
   * (e.g. `?entitatiSilvice=,A`) is the null sentinel: include photos
   * whose entitateSilvica is NULL alongside the listed values.
   */
  entitatiSilvice: z.string().optional(),
  /**
   * Location filters mirroring the parcel ones — applied against the
   * photo's own denormalised columns (set on import + capture). Each
   * accepts a CSV multi-select; an empty slot is the null sentinel
   * for "include rows where the column is NULL". Used by /app/filter
   * so the photo layer and results table narrow alongside polygons.
   */
  primarii: z.string().optional(),
  ocoaleSilvice: z.string().optional(),
  /**
   * Technical filters mirroring the parcel ones. `codProiect`/`ucf`
   * are case-insensitive substring matches (single value); the CSV
   * plural variants `codProiecte`/`ucfs` are exact `IN (...)` lookups
   * for multi-select pickers. `suprafataMin`/`suprafataMax` are an
   * inclusive range on the photo's recorded suprafata.
   */
  codProiect: z.string().optional(),
  codProiecte: z.string().optional(), // csv; takes precedence over `codProiect`
  ucf: z.string().optional(),
  ucfs: z.string().optional(), // csv; takes precedence over `ucf`
  /**
   * ID proiect (`Parcel.projectId` / `ID_Cod`), case-insensitive
   * substring. Polygon-only: PhotoRecord has no such column, so this
   * matches through the linked parcel and never a loose photo.
   */
  projectId: z.string().optional(),
  suprafataMin: z.coerce.number().optional(),
  suprafataMax: z.coerce.number().optional(),
  hasParcel: booleanParam.optional(),
  /**
   * Viewport filter as `minLon,minLat,maxLon,maxLat`.
   *
   * The map draws one marker per returned row, so without this the
   * marker layer showed only the first `limit` photos of the whole
   * result set — a fixed sample of the country rather than what is on
   * screen. With a bbox the cap applies per viewport instead.
   */
  bbox: z.string().optional(),
  polygonStatus: z.string().optional(),
  polygonStatuses: z.string().optional(), // csv; takes precedence over `polygonStatus`
  includeDeleted: z.coerce.boolean().optional().default(false),
  sort: z
    .enum([
      'createdAt',
      'nr',
      'fileName',
      'raion',
      'anul',
      'year',
      'latitude',
      'longitude',
      'polygonStatus',
      'authorCod',
    ])
    .default('createdAt'),
  order: z.enum(['asc', 'desc']).default('desc'),
});
export type PhotoRecordListQuery = z.infer<typeof PhotoRecordListQuerySchema>;

export const PhotoRecordListResponseSchema = z.object({
  items: z.array(PhotoRecordSchema),
  pagination: PaginationMetaSchema,
  totals: z.object({
    totalPhotos: z.number().int(),
    withParcel: z.number().int(),
    withoutParcel: z.number().int(),
  }),
});
export type PhotoRecordListResponse = z.infer<typeof PhotoRecordListResponseSchema>;

/**
 * downloadUrl must be an http(s) URL. Rejecting other schemes (notably
 * `javascript:`) at the contract boundary prevents a stored value from
 * later executing when rendered into an anchor `href`.
 */
export const DownloadUrlSchema = z
  .string()
  .min(1, 'downloadUrl required')
  .max(2000)
  .refine((v) => /^https?:\/\//i.test(v.trim()), {
    message: 'downloadUrl must start with http:// or https://',
  });

export const PhotoRecordCreateInputSchema = z
  .object({
    fileName: z.string().min(1, 'fileName required').max(500),
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    gpsAccuracyM: z.number().nonnegative().nullable().optional(),
    downloadUrl: DownloadUrlSchema,
    parcelId: z.string().uuid().nullable().optional(),
    ucf: z.string().max(50).nullable().optional(),
    suprafata: z.number().nonnegative().nullable().optional(),
    raion: z.string().max(100).nullable().optional(),
    primarie: z.string().max(100).nullable().optional(),
    ocolSilvic: z.string().max(100).nullable().optional(),
    entitateSilvica: z.string().max(100).nullable().optional(),
    speciaDeBaza: z.string().max(16).nullable().optional(),
    anulCultura: z.number().int().min(2000).max(2100).nullable().optional(),
    ultimaLucrare: z.string().max(400).nullable().optional(),
    codPrimarie: z.string().max(32).nullable().optional(),
    /** @deprecated Use `year`. */
    anul: z.number().int().min(1900).max(2200).nullable().optional(),
    year: z.number().int().min(1900).max(2200).nullable().optional(),
    month: z.number().int().min(1).max(12).nullable().optional(),
    project: z.string().trim().min(1).max(100).optional(),
    projectNote: z.string().max(500).nullable().optional(),
    codProiect: z.string().max(200).nullable().optional(),
  })
  .strict()
  .refine(
    (v) =>
      v.project === 'Alte' ||
      v.projectNote === undefined ||
      v.projectNote === null ||
      v.projectNote === '',
    { message: 'projectNote is only allowed when project = Alte', path: ['projectNote'] },
  );
export type PhotoRecordCreateInput = z.infer<typeof PhotoRecordCreateInputSchema>;

export const PhotoRecordUpdateInputSchema = z
  .object({
    latitude: z.number().min(-90).max(90).optional(),
    longitude: z.number().min(-180).max(180).optional(),
    downloadUrl: DownloadUrlSchema.optional(),
    parcelId: z.string().uuid().nullable().optional(),
    ucf: z.string().max(50).nullable().optional(),
    suprafata: z.number().nonnegative().nullable().optional(),
    raion: z.string().max(100).nullable().optional(),
    primarie: z.string().max(100).nullable().optional(),
    ocolSilvic: z.string().max(100).nullable().optional(),
    entitateSilvica: z.string().max(100).nullable().optional(),
    speciaDeBaza: z.string().max(16).nullable().optional(),
    anulCultura: z.number().int().min(2000).max(2100).nullable().optional(),
    ultimaLucrare: z.string().max(400).nullable().optional(),
    codPrimarie: z.string().max(32).nullable().optional(),
    /** @deprecated Use `year`. */
    anul: z.number().int().min(1900).max(2200).nullable().optional(),
    year: z.number().int().min(1900).max(2200).nullable().optional(),
    month: z.number().int().min(1).max(12).nullable().optional(),
    project: z.string().trim().min(1).max(100).optional(),
    projectNote: z.string().max(500).nullable().optional(),
    codProiect: z.string().max(200).nullable().optional(),
    /** SUPER_ADMIN only — server enforces. */
    authorId: z.string().uuid().nullable().optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, {
    message: 'At least one field must be provided',
  })
  .refine(
    (v) =>
      v.project === undefined ||
      v.project === 'Alte' ||
      v.projectNote === undefined ||
      v.projectNote === null ||
      v.projectNote === '',
    { message: 'projectNote is only allowed when project = Alte', path: ['projectNote'] },
  );
export type PhotoRecordUpdateInput = z.infer<typeof PhotoRecordUpdateInputSchema>;

/** Shape returned by GET /api/photos/:id/nearby-parcels */
export const NearbyParcelItemSchema = z.object({
  id: z.string().uuid(),
  projectUcfKey: z.string(),
  raion: z.string(),
  primarie: z.string(),
  ucf: z.string(),
  codProiect: z.string().nullable().optional(),
  suprafata: z.number(),
  anul: z.number().int(),
  distanceKm: z.number(),
  /**
   * True when the photo's GPS point falls inside this parcel's polygon.
   * The server pins containing parcels to the top of the list with
   * distanceKm = 0 so the dialog surfaces them ahead of merely-close
   * neighbours. Without this flag, a long narrow trupuri that contains
   * the photo can be ranked below a small round parcel whose centroid
   * happens to be closer to the GPS.
   */
  contains: z.boolean().optional(),
});
export type NearbyParcelItem = z.infer<typeof NearbyParcelItemSchema>;

export const NearbyParcelsResponseSchema = z.object({
  items: z.array(NearbyParcelItemSchema),
});
export type NearbyParcelsResponse = z.infer<typeof NearbyParcelsResponseSchema>;

export const ParcelCentroidResponseSchema = z.object({
  parcelId: z.string().uuid(),
  latitude: z.number(),
  longitude: z.number(),
});
export type ParcelCentroidResponse = z.infer<typeof ParcelCentroidResponseSchema>;
